'use client';

import { type ToNever, useIntl } from '@capsicum/common';
import React, { type MouseEventHandler, type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import styled, { type RuleSet } from 'styled-components';

import { useRTL } from '../../../hooks/helpers/useRTL';
import { CarouselArrow } from '../../atoms';
import { HeaderBlock, type HeaderBlockProps } from '../header-block/HeaderBlock';

type HeaderProps = Pick<HeaderBlockProps, 'onActionClick' | 'title'>;

/**
 * Require the header props in case showHeader is true or undefined. Vice-versa if showHeader is false.
 */
type DiscriminatedUnion = (HeaderProps & { showHeader?: true }) | (ToNever<HeaderProps> & { showHeader: false });
type CarouselVariant = 'default' | 'fullWidth';

export type CardCarouselProps = PropsWithChildren<{
	allLinkClick?: MouseEventHandler<HTMLButtonElement>;
	arrowsVisible?: boolean;
	cardWidth: number;
	cardWrapperStyle?: RuleSet<object>;
	disabled?: boolean;
	testID: string;
	title?: string;
	variant?: CarouselVariant;
}> &
	DiscriminatedUnion;

export const CardCarousel = ({
	allLinkClick,
	arrowsVisible = true,
	cardWidth,
	cardWrapperStyle,
	children,
	disabled = false,
	testID,
	title,
	variant = 'default',
	...props
}: CardCarouselProps) => {
	const [isDown, setIsDown] = useState<boolean>(false);
	const [startX, setStartX] = useState<number>(0);
	const [scrollLeft, setScrollLeft] = useState<number>(0);
	const [reachedEnd, setReachedEnd] = useState<boolean>();
	const [reachedStart, setReachedStart] = useState<boolean>();
	const carouselWrapperRef = useRef<HTMLDivElement | null>(null);
	const { $t } = useIntl();
	const { isRTL } = useRTL();

	const scroll = useCallback(
		(direction: 'left' | 'right') => {
			if (disabled) return;

			const rtlSign = isRTL ? -1.3 : 1.3;

			carouselWrapperRef?.current?.scrollBy({
				behavior: 'smooth',
				left: direction === 'left' ? -cardWidth * rtlSign : cardWidth * rtlSign
			});
		},
		[cardWidth, isRTL]
	);

	const mouseMove = (e: React.MouseEvent) => {
		if (!isDown || disabled) return;
		e.preventDefault();
		const x = e.pageX - carouselWrapperRef.current!.offsetLeft;
		const walk = (x - startX) * 2; // Scroll-fast

		carouselWrapperRef.current!.scrollLeft = scrollLeft - walk;
	};

	const mouseDown = (e: React.MouseEvent) => {
		if (disabled) return;
		setIsDown(true);
		setStartX(e.pageX - carouselWrapperRef.current!.offsetLeft);
		setScrollLeft(carouselWrapperRef.current!.scrollLeft);
	};

	useEffect(() => {
		if (disabled) return;

		const getObserver = (setterMethod: (arg0: boolean) => void) =>
			new IntersectionObserver(entries => setterMethod(!(entries[0]!.intersectionRatio < 0.98)), {
				root: carouselWrapperRef.current,
				threshold: [0, 0.98]
			});

		const firstCardObserver = getObserver(val => setReachedStart(val));
		const lastCardObserver = getObserver(val => setReachedEnd(val));

		const carouselChildren = Array.from(carouselWrapperRef.current?.children ?? []);
		const firstChild = carouselChildren.at(0);
		const lastChild = carouselChildren.at(-1);

		if (firstChild) {
			firstCardObserver.observe(firstChild);
		}

		if (lastChild) {
			lastCardObserver.observe(lastChild);
		}

		return () => {
			// Clean up the observers
			firstCardObserver.disconnect();
			lastCardObserver.disconnect();
		};
	});

	return (
		<S.Carousel $variant={variant} data-testid={`${testID}`} {...props}>
			{arrowsVisible && (
				<S.ArrowWrapper
					$isRTL={isRTL}
					$position="left"
					$reachedStart={reachedStart}
					role="button"
					onClick={() => scroll('left')}
				>
					<CarouselArrow direction={isRTL ? 'right' : 'left'} />
				</S.ArrowWrapper>
			)}
			{title && (
				<HeaderBlock
					actionText={$t('global.seeAll')}
					testID={`${testID}-header`}
					title={title}
					onActionClick={allLinkClick}
				/>
			)}
			<S.CardsWrapper
				$cardWidth={cardWidth}
				$cardWrapperStyle={cardWrapperStyle}
				$isRTL={isRTL}
				ref={carouselWrapperRef}
				onMouseDown={mouseDown}
				onMouseLeave={() => setIsDown(false)}
				onMouseMove={mouseMove}
				onMouseUp={(e: { stopPropagation: () => void }) => {
					if (disabled) return;
					e.stopPropagation();
					setIsDown(false);
				}}
			>
				{children}
			</S.CardsWrapper>
			{arrowsVisible && (
				<S.ArrowWrapper
					$isRTL={isRTL}
					$position="right"
					$reachedEnd={reachedEnd}
					role="button"
					onClick={() => scroll('right')}
				>
					<CarouselArrow direction={isRTL ? 'left' : 'right'} />
				</S.ArrowWrapper>
			)}
		</S.Carousel>
	);
};

namespace S {
	export const Carousel = styled.div<{ $variant: CarouselVariant }>`
		position: relative;
		display: flex;
		flex-direction: column;
		width: auto;
		${({ $variant, theme }) =>
			$variant === 'default' &&
			`
		padding: 0 ${theme.spacings.xxlarge};
		margin: 0 ${theme.spacings.xxlarge};
		`}
	`;

	export const CardsWrapper = styled.div<{
		$cardWidth: number;
		$cardWrapperStyle?: RuleSet<object>;
		$isRTL?: boolean;
	}>`
		display: flex;
		flex-direction: row;
		overflow: auto;
		${({ $cardWrapperStyle }) => $cardWrapperStyle};

		&::-webkit-scrollbar {
			display: none; /* Safari and Chrome */
		}

		-ms-overflow-style: none; /* Internet Explorer 10+ */
		scrollbar-width: none; /* Firefox */

		> *:not(:nth-child(1)) {
			margin-left: ${({ $isRTL, theme }) => ($isRTL ? 0 : theme.spacings.small)};
			margin-right: ${({ $isRTL, theme }) => ($isRTL ? theme.spacings.small : 0)};
		}

		> * {
			flex: 0 0 ${({ $cardWidth }) => $cardWidth}px;
		}

		/* Negative margin to escape wrapper padding */
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin: 0 -${({ theme }) => theme.spacings.small};
			padding: 0 ${({ theme }) => theme.spacings.small};
		}

		${({ theme }) => theme.breakpoints.queries.only.largeDesktop} {
			> *:not(:nth-child(1)) {
				margin-left: ${({ $isRTL, theme }) => ($isRTL ? 0 : theme.spacings.large)};
				margin-right: ${({ $isRTL, theme }) => ($isRTL ? theme.spacings.large : 0)};
			}
		}
	`;

	export const ArrowWrapper = styled.div<{
		$isRTL: boolean;
		$position: 'left' | 'right';
		$reachedEnd?: boolean;
		$reachedStart?: boolean;
	}>`
		z-index: 20;
		${({ theme }) => theme.border.radius.circle};
		height: ${({ theme }) => theme.sizing.large};
		width: ${({ theme }) => theme.sizing.large};
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		cursor: pointer;

		${({ $isRTL, $position, theme }) => {
			const offset = theme.sizing.xxsmall;

			return $position === 'left'
				? $isRTL
					? `right: -${offset};`
					: `left: -${offset};`
				: $position === 'right'
					? $isRTL
						? `left: -${offset};`
						: `right: -${offset};`
					: '';
		}};

		display: ${({ $position, $reachedEnd, $reachedStart }) => {
			if ($position === 'left' && $reachedStart) {
				return 'none';
			} else if ($position === 'right' && $reachedEnd) {
				return 'none';
			} else return 'flex';
		}};
		align-items: center;
		justify-content: center;

		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			display: none;
		}
	`;
}
